import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useStores } from 'util/mobx/stores';
import MaterialDrawerDetailNav from 'components/MaterialDrawerDetailNav';
import { MaterialDTO } from 'dto/material';
import { GuideMaterialLikeDTO } from 'dto/guide';
import { SurgeryGuideMaterialLikeDTO } from 'dto/surgeryGuide';
import { CSSTransition } from 'react-transition-group';
import { Can, subjectArea } from 'casl/setupCaslAbility';
import Button from 'components/Form/Button';
import CreateMaterialSynonymFlyout from 'components/MaterialKnowledge/CreateMaterialSynonymFlyout';
import { subject } from '@casl/ability';
import GuideSettings from '../GuideSettings';
import FixedTopMaterialBox from './FixedTopMaterialBox';
import MaterialHead from './MaterialHead';
import DrawerMaterialContent from './DrawerMaterialContent';

interface Props {
  material: MaterialDTO;
  guideMaterial?: GuideMaterialLikeDTO;
  surgeryGuideMaterial?: SurgeryGuideMaterialLikeDTO;
  onUpdateGuideMaterial: (guideMaterial?: GuideMaterialLikeDTO, surgeryGuideMaterial?: SurgeryGuideMaterialLikeDTO) => void;
}

const MaterialDrawerContent = observer(({ material, guideMaterial, surgeryGuideMaterial, onUpdateGuideMaterial }: Props) => {
  const {
    guideStore,
    materialStore,
    guideDetailDrawerStore,
    materialSetsStore,
    procedureStore,
    knowledgeStore,
    searchStore,
    domainStore
  } = useStores();
  const [isShow, setIsShow] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(window.innerWidth >= 991);
  const { t } = useTranslation('guideSettings');

  useEffect(() => {
    materialStore.loadMaterial(material.materialId);
  }, [materialStore, knowledgeStore, material.materialId]);

  const handleContentWrapperScroll = (event: React.UIEvent<HTMLElement>) => {
    if (!isShow && event.currentTarget.scrollTop >= 300) {
      setIsShow(true);
    } else if (isShow && event.currentTarget.scrollTop <= 299 && event.currentTarget.scrollTop !== 0) {
      setIsShow(false);
    }
  };

  const onUpdateFile = async (fileId: string) => {
    await materialStore.updateMaterial({ materialId: material.materialId, pictureFileId: fileId });
    guideDetailDrawerStore.reloadSelectedMaterial();
    if (guideStore.selectedGuide) {
      procedureStore.loadProcedures(guideStore.selectedGuide.guideId);
    }
    if (searchStore.isSearchPanelOpen) {
      searchStore.refreshSearch();
    }
    if (materialSetsStore.materialSetId) {
      materialSetsStore.loadMaterialSet(materialSetsStore.materialSetId);
    }
  };

  return (
    <div className="div-block-14">
      <div className="div-block-12">
        <CreateMaterialSynonymFlyout
          isOpen={guideDetailDrawerStore.isCreateMaterialSynonymOpen}
          onClose={() => guideDetailDrawerStore.closeCreateMaterialSynonymFlyout()}
        />
        <div className="mat_content_wrapper" onScroll={handleContentWrapperScroll}>
          <FixedTopMaterialBox material={material} isShow={isShow} onUpdateFile={onUpdateFile} />
          <div className="div-block-21" onScroll={handleContentWrapperScroll}>
            <MaterialHead material={material} onUpdateFile={onUpdateFile} />
            {materialStore.materialDetail && <DrawerMaterialContent key={material.materialId} material={material} />}
          </div>
        </div>
        {(guideMaterial || surgeryGuideMaterial) && (
          <Can I="see" this={subject(subjectArea.materialSettings, { departmentId: domainStore.currentDepartment.id })}>
            <CSSTransition in={isSettingsOpen} timeout={500} classNames="settings-drawer" unmountOnExit>
              <GuideSettings
                guideMaterial={guideMaterial}
                surgeryGuideMaterial={surgeryGuideMaterial}
                onSettingsClose={() => setIsSettingsOpen(false)}
              />
            </CSSTransition>
          </Can>
        )}
      </div>

      <div className="div-block-13">
        <MaterialDrawerDetailNav
          onUpdateFile={onUpdateFile}
          detailLink={`/material/overview?materialId=${material.materialId}`}
          knowledgeLink={`/material/content?materialId=${material.materialId}`}
          onShowSettings={() => setIsSettingsOpen(true)}
          isGuideMaterialLike={!!guideMaterial}
        />
        <Button onClick={() => onUpdateGuideMaterial(guideMaterial, surgeryGuideMaterial)} className="btn_big_cancel active">
          <div>{t('button.close')}</div>
        </Button>
      </div>
    </div>
  );
});

export default MaterialDrawerContent;
